/**
 * PRD.305 — the laboratory order detail drawer.
 *
 * Shared by the Laboratory module and (under Full Management) by Patient
 * Workspace / Consultation Labs, so an order behaves identically wherever it is
 * opened. Every action it offers is filtered through the caller's resolved
 * capabilities — module ∩ Laboratory Settings mode ∩ RBAC — and every write
 * additionally re-checks the pure guard in the service layer, so a stale drawer
 * can never push an illegal transition.
 *
 * PRD §17: upload and publish are deliberately different actions with different
 * weight. Uploading a file changes nothing clinically; Publish is the explicit
 * act that makes a result available, and it is styled as the primary action and
 * confirmed before it fires.
 */
import { useCallback, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Drawer } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import { useAuth } from '@helios/auth-firebase-web';
import {
  qLabOrderTests,
  qLabResults,
  qLabResultFilesForTest,
  aggregateLabOrderStatus,
  canAdvanceLabTest,
  canPublishLabResult,
  canUploadLabResult,
  canCancelLabTest,
  canCorrectLabResult,
  partitionLabArtifacts,
  type LabOrderRow,
  type LabOrderTestRow,
  type LabResultRow,
  type LabResultFileRow,
  type LaboratoryCapabilities,
  type LabTestStatus,
} from '@helios/data-access';
import { usePowerSyncDb, usePowerSyncQuery } from '../../adapters/powersync';
import { useBrandTokens } from '../../shell/shared';
import { useFormatMoney } from '../../hooks/useFormatMoney';
import { uploadToStorage } from '../../lib/upload-to-storage';
import {
  advanceLabTestStatus,
  attachLabResultFiles,
  publishLabResult,
  cancelLabTest,
  correctLabResult,
  type UploadedLabArtifact,
} from '../../services/laboratory-service';
import {
  LabStatusBadge,
  LabPaymentBadge,
  LabUnpublishedBadge,
  formatLabDate,
  parseTestStatuses,
} from './lab-shared';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  order: LabOrderRow | null;
  capabilities: LaboratoryCapabilities;
}

const sectionTitle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-semi)',
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.6px',
  margin: '0 0 var(--sp-2)',
};

/** The next status offered for a test, or null when it has nowhere to go. */
function nextStatusFor(status: string | null): LabTestStatus | null {
  switch (status) {
    case 'ordered':
      return 'collected';
    case 'collected':
      return 'in_progress';
    case 'in_progress':
      return 'result_ready';
    default:
      // result_ready advances only via Publish; published/cancelled are terminal.
      return null;
  }
}

const NEXT_LABEL: Record<string, string> = {
  collected: 'Mark Collected',
  in_progress: 'Start Processing',
  result_ready: 'Mark Result Ready',
};

function TestCard({
  test,
  capabilities,
  patientId,
  orgId,
  onBusyChange,
  onError,
}: {
  test: LabOrderTestRow;
  capabilities: LaboratoryCapabilities;
  patientId: string;
  orgId: string;
  onBusyChange: (busy: boolean) => void;
  onError: (message: string | null) => void;
}) {
  const { db } = usePowerSyncDb();
  const { user } = useAuth();
  const formatMoney = useFormatMoney();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [expanded, setExpanded] = useState(false);

  const actor = useMemo(
    () => ({ uid: user?.uid ?? '', name: user?.displayName ?? user?.email ?? null }),
    [user],
  );

  const { data: results = [] } = usePowerSyncQuery<LabResultRow>(qLabResults(test.id));
  const { data: artifactRows = [] } = usePowerSyncQuery<LabResultFileRow>(
    qLabResultFilesForTest(test.id),
  );
  const { published, unpublished } = useMemo(
    () => partitionLabArtifacts(artifactRows),
    [artifactRows],
  );

  const draftResult = useMemo(() => results.find((r) => r.published_at == null) ?? null, [results]);
  const publishedResults = useMemo(() => results.filter((r) => r.published_at != null), [results]);

  const next = nextStatusFor(test.status);
  const canAdvance =
    capabilities.advanceStatus &&
    next != null &&
    canAdvanceLabTest({ status: test.status }, next).ok;
  const canUpload = capabilities.uploadResult && canUploadLabResult({ status: test.status }).ok;
  const publishVerdict = draftResult
    ? canPublishLabResult({
        test: { status: test.status },
        result: { published_at: draftResult.published_at, source: draftResult.source },
        attachedFileCount: Number(draftResult.file_count ?? 0),
      })
    : { ok: false, reason: 'Upload a result file first.' };
  const canPublish = capabilities.publishResult && publishVerdict.ok;
  const canCancel = capabilities.cancelOrder && canCancelLabTest({ status: test.status }).ok;

  const run = useCallback(
    async (fn: () => Promise<void>) => {
      onBusyChange(true);
      onError(null);
      try {
        await fn();
      } catch (err) {
        console.error('[LabOrderDrawer] action failed:', err);
        onError(err instanceof Error ? err.message : 'That action could not be completed.');
      } finally {
        onBusyChange(false);
      }
    },
    [onBusyChange, onError],
  );

  const handleUpload = useCallback(
    async (files: FileList | null) => {
      if (!db || !files || files.length === 0) return;
      await run(async () => {
        const artifacts: UploadedLabArtifact[] = [];
        for (const file of Array.from(files)) {
          // Pre-generate the id so the Storage folder and patient_files.id match
          // — the same contract the Files upload path uses.
          const fileId = crypto.randomUUID();
          const uploaded = await uploadToStorage({ orgId, patientId, file, fileId });
          artifacts.push({
            fileId,
            filename: uploaded.filename,
            url: uploaded.downloadUrl,
            mimeType: uploaded.mimeType,
            sizeBytes: file.size,
            kind: file.type.startsWith('image/') ? 'image' : 'document',
          });
        }
        await attachLabResultFiles({
          db,
          orgId,
          patientId,
          testId: test.id,
          resultId: draftResult?.id ?? null,
          artifacts,
          actor,
        });
      });
    },
    [db, orgId, patientId, test.id, draftResult, actor, run],
  );

  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--sp-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-3)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--sp-3)' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 'var(--font-size-base)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
            }}
          >
            {test.test_name}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-2)',
              marginTop: 'var(--sp-2)',
              flexWrap: 'wrap',
            }}
          >
            <LabStatusBadge status={test.status} />
            <LabPaymentBadge status={test.invoice_payment_status} />
            {unpublished.length > 0 && <LabUnpublishedBadge />}
          </div>
        </div>
        {test.price_cents_snapshot != null && (
          <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
            {formatMoney(test.price_cents_snapshot / 100)}
          </div>
        )}
      </div>

      {/* Results */}
      {(published.length > 0 || unpublished.length > 0) && (
        <div>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-primary)',
            }}
          >
            {expanded ? 'Hide' : 'Show'} {published.length + unpublished.length} file
            {published.length + unpublished.length === 1 ? '' : 's'}
          </button>
          {expanded && (
            <div
              style={{
                marginTop: 'var(--sp-2)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--sp-1)',
              }}
            >
              {[...published, ...unpublished].map((a) => (
                <a
                  key={a.linkId}
                  href={a.url ?? '#'}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 'var(--sp-2)',
                    padding: 'var(--sp-2)',
                    borderRadius: 'var(--radius-xs)',
                    background: 'var(--bg-surface-2)',
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--text)',
                    textDecoration: 'none',
                  }}
                >
                  <span
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {a.filename}
                  </span>
                  {!a.published && <LabUnpublishedBadge />}
                </a>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
        {canAdvance && next && (
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              run(() => advanceLabTestStatus({ db, testId: test.id, to: next, actor }))
            }
          >
            {NEXT_LABEL[next] ?? 'Advance'}
          </Button>
        )}

        {canUpload && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={(e) => {
                handleUpload(e.target.files);
                e.target.value = '';
              }}
            />
            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
              Upload Result
            </Button>
          </>
        )}

        {capabilities.publishResult && draftResult && (
          <Button
            variant="gradient"
            size="sm"
            disabled={!canPublish}
            title={canPublish ? undefined : publishVerdict.reason}
            onClick={() => {
              if (
                !window.confirm(
                  'Publish this result? Published results become part of the clinical record and cannot be edited or replaced — a correction can be issued later.',
                )
              )
                return;
              run(() => publishLabResult({ db, resultId: draftResult.id, actor }));
            }}
          >
            Publish Result
          </Button>
        )}

        {capabilities.publishResult && publishedResults.some((r) => canCorrectLabResult(r).ok) && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const target = publishedResults.find((r) => canCorrectLabResult(r).ok);
              if (!target) return;
              const reason = window.prompt('Reason for the correction (optional)') ?? null;
              run(async () => {
                await correctLabResult({ db, orgId, resultId: target.id, actor, reason });
              });
            }}
          >
            Issue Correction
          </Button>
        )}

        {canCancel && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const reason = window.prompt('Reason for cancelling this test (optional)') ?? null;
              run(() => cancelLabTest({ db, testId: test.id, actor, reason }));
            }}
          >
            Cancel Test
          </Button>
        )}
      </div>

      {test.cancel_reason && (
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
          Cancelled: {test.cancel_reason}
        </div>
      )}
    </div>
  );
}

export function LabOrderDrawer({ open, onOpenChange, order, capabilities }: Props) {
  const brandTokens = useBrandTokens();
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: tests = [] } = usePowerSyncQuery<LabOrderTestRow>(
    qLabOrderTests(order?.id ?? '__none__'),
  );

  const aggregate = useMemo(
    () => aggregateLabOrderStatus(order ? parseTestStatuses(order.test_statuses) : []),
    [order],
  );

  if (!order) return null;

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={order.order_number ?? 'Lab Order'}
      description={order.patient_name ?? undefined}
      width={560}
      portalStyle={brandTokens}
      badges={<LabStatusBadge status={aggregate} prefix="Order" />}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>
        {error && (
          <div
            role="alert"
            style={{
              padding: 'var(--sp-3)',
              borderRadius: 'var(--radius-md)',
              background: '#fee2e2',
              color: '#991b1b',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            {error}
          </div>
        )}

        <div>
          <h3 style={sectionTitle}>Order</h3>
          <dl
            style={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              gap: 'var(--sp-2) var(--sp-4)',
              margin: 0,
              fontSize: 'var(--font-size-sm)',
            }}
          >
            <dt style={{ color: 'var(--text-muted)' }}>Ordered</dt>
            <dd style={{ margin: 0, color: 'var(--text)' }}>{formatLabDate(order.ordered_at)}</dd>
            <dt style={{ color: 'var(--text-muted)' }}>Ordered by</dt>
            <dd style={{ margin: 0, color: 'var(--text)' }}>
              {order.provider_name ?? order.ordered_by_name ?? '—'}
            </dd>
            {order.notes && (
              <>
                <dt style={{ color: 'var(--text-muted)' }}>Notes</dt>
                <dd style={{ margin: 0, color: 'var(--text)' }}>{order.notes}</dd>
              </>
            )}
          </dl>
        </div>

        <div>
          <h3 style={sectionTitle}>
            Tests ({tests.length}){busy ? ' · working…' : ''}
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
            {tests.map((test) => (
              <TestCard
                key={test.id}
                test={test}
                capabilities={capabilities}
                patientId={order.patient_id ?? ''}
                orgId={orgId}
                onBusyChange={setBusy}
                onError={setError}
              />
            ))}
          </div>
        </div>
      </div>
    </Drawer>
  );
}

export default LabOrderDrawer;
